import React, { useCallback, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { TextField, Autocomplete, Typography, Tooltip } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  AutocompleteRenderInputParams,
  AutocompleteInputChangeReason
} from '@mui/material/Autocomplete';
import { LabelOrigin, LabelType, TimeSignal } from 'graphql/types';
import {
  NOTE_FIELDS,
  NOTE_MAX_CHAR_LIMIT
} from 'modules/notes/common/noteSchema';
import { CharacterCounter } from 'modules/common/components';
import {
  useFetchSuggestions,
  FetchSuggestionsHandler
} from 'modules/notes/addNote/hooks';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks';
import { mapLabelValuesToTimesheetLabels } from 'modules/notes/common/utils';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(0, 2.5, 3, 0)
  },
  characterCount: {
    margin: '0 !important',
    paddingTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(1)
  },
  sources: {},
  clearButton: {
    padding: theme.spacing(0.25)
  },
  endAdornment: {
    position: 'relative'
  },
  adornmentContainer: {
    display: 'flex'
  }
}));

export type DescriptionChangeProps = {
  fetchSuggestions: FetchSuggestionsHandler;
  description: string | undefined;
};

type GroupEditDescriptionProps = {
  onDescriptionChange: (props: DescriptionChangeProps) => void;
  notes: TimeSignal[];
  autoFocus?: boolean;
  onOptionChange: (signal: TimeSignal) => void;
};

export const GroupEditDescription: React.FC<GroupEditDescriptionProps> = ({
  onDescriptionChange,
  onOptionChange,
  notes,
  autoFocus = false
}: GroupEditDescriptionProps) => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const classes = useStyles();
  const filteredOptions = useMemo(() => notes.filter((x) => x.description), [
    notes
  ]);
  const noteDescription = useWatch({ name: NOTE_FIELDS.NOTE });
  const [selected, setSelected] = useState<TimeSignal | undefined>(
    filteredOptions[0]
  );
  const characterCountClasses = useMemo(
    () => ({
      root: classes.characterCount
    }),
    [classes.characterCount]
  );
  const {
    setValue,
    getValues,
    reset,
    formState: { errors }
  } = useFormContext();

  const getInputComponent = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <div className={classes.adornmentContainer}>
              <div className={classes.clearButton}>
                {params.InputProps.endAdornment}
              </div>

              <CharacterCounter
                text={noteDescription}
                hasError={
                  Boolean(errors[NOTE_FIELDS.NOTE]) ||
                  noteDescription?.length > NOTE_MAX_CHAR_LIMIT
                }
                classes={characterCountClasses}
                totalChar={NOTE_MAX_CHAR_LIMIT}
              />
            </div>
          )
        }}
        data-testid="group-edit-description-input"
        autoFocus={autoFocus}
        size="small"
      />
    ),
    [
      classes.adornmentContainer,
      classes.clearButton,
      noteDescription,
      errors,
      characterCountClasses,
      autoFocus
    ]
  );

  const fetchSuggestions = useFetchSuggestions();

  const onChange = useCallback(
    (_, option) => {
      if (!option) return;
      const signal = option as TimeSignal;
      setSelected(signal);

      const values = getValues();
      const timesheetLabels = mapLabelValuesToTimesheetLabels({
        isSmartSuggestionEnabled,
        labelValues: (signal.labelValues || []).filter(
          (value) => value.label.origin === LabelOrigin.Timesheet
        )
      });
      const customLabels = mapLabelValuesToTimesheetLabels({
        isSmartSuggestionEnabled,
        labelValues:
          signal?.labelValues?.filter(
            (value) => value.label.origin === LabelOrigin.User
          ) || []
      });
      onOptionChange(signal);
      reset(
        {
          ...values,
          [NOTE_FIELDS.NOTE]: signal.description,
          [NOTE_FIELDS.TIMESHEET_LABELS]: timesheetLabels.map((label) => {
            const commentOverrideValue =
              label.type === LabelType.Comment
                ? { value: { stringValue: signal.description } }
                : {};
            return { label: { ...label, ...commentOverrideValue } };
          }),
          [NOTE_FIELDS.CUSTOM_LABELS]: customLabels.filter(
            (label) => label.value
          ),
          sources: signal.sources,
          [NOTE_FIELDS.NOTE_ID]: signal.id
        },
        {
          keepErrors: true,
          keepDirty: true
        }
      );
    },

    [getValues, isSmartSuggestionEnabled, onOptionChange, reset]
  );

  const onInputChange = useCallback(
    (_, value: string, reason: AutocompleteInputChangeReason) => {
      switch (reason) {
        case 'clear':
          {
            setValue(NOTE_FIELDS.NOTE, undefined, {
              shouldDirty: true
            });
          }
          break;
        case 'input':
          {
            setValue(NOTE_FIELDS.NOTE, value, {
              shouldDirty: true
            });
            onDescriptionChange({ fetchSuggestions, description: value });
          }
          break;

        default:
          break;
      }
    },
    [setValue, onDescriptionChange, fetchSuggestions]
  );

  const renderOption = useCallback((props, option: TimeSignal) => {
    return (
      <li {...props} key={option.id}>
        <Tooltip title={option.description ?? ''} disableInteractive>
          <Typography
            variant="inherit"
            noWrap
            data-testid={`note-option-${option.description}`}
          >
            {option.description}
          </Typography>
        </Tooltip>
      </li>
    );
  }, []);

  const getOptionLabel = useCallback(
    (option: TimeSignal) => option.description || '',
    []
  );

  const autocompleteClasses = useMemo(
    () => ({ endAdornment: classes.endAdornment, root: classes.root }),
    [classes.endAdornment, classes.root]
  );

  return (
    <Autocomplete
      classes={autocompleteClasses}
      freeSolo
      data-testid="group-edit-description"
      options={filteredOptions}
      renderInput={getInputComponent}
      onChange={onChange}
      renderOption={renderOption}
      onInputChange={onInputChange}
      value={selected}
      getOptionLabel={getOptionLabel}
      openOnFocus
    />
  );
};
